import { Link } from 'react-router-dom';
import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { aboutAdvantages, reputationComments, aboutStats } from '@/mocks/about';

const FORM_URL = 'http://cgshe.cn/';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于黑料吃瓜网',
  description: '黑料吃瓜网 - 全网最新网红黑料与娱乐八卦资讯平台。了解我们的品牌定位、核心优势和联系方式。',
  url: 'http://cgshe.cn/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '黑料吃瓜网',
    url: 'http://cgshe.cn/',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'http://cgshe.cn/' },
    { '@type': 'ListItem', position: 2, name: '关于我们' },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '黑料吃瓜网的内容来源是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '黑料吃瓜网的内容来源于全网社交平台公开信息、网友投稿以及专业娱乐记者的一手资讯。我们对每条爆料都进行多方核实，确保信息真实可靠。',
      },
    },
    {
      '@type': 'Question',
      name: '如何向黑料吃瓜网投稿爆料？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过我们的官方邮箱 hello@cgshe.cn 进行投稿，也可以在微博、微信公众号私信我们。我们承诺对爆料人信息严格保密。',
      },
    },
    {
      '@type': 'Question',
      name: '黑料吃瓜网的更新频率是怎样的？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '黑料吃瓜网实行7×24小时不间断更新机制，编辑团队全天候监控全网热点，确保您在任何时间访问都能看到最新内容。每天平均更新300+条资讯。',
      },
    },
    {
      '@type': 'Question',
      name: '黑料吃瓜网如何保证信息真实性？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们拥有一支专业的审核团队，每条爆料发布前都会经过至少两轮交叉验证。对于未经证实的信息，我们会明确标注"待核实"，绝不传播虚假内容。',
      },
    },
  ],
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotVal = (formData.get('company_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = (parsed as { meta?: { message?: string } }).meta?.message
          || (parsed as { message?: string }).message
          || responseText
          || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 黑料吃瓜网 | 品牌介绍与联系方式"
        description="黑料吃瓜网品牌介绍，了解我们的平台定位、核心优势和联系方式。全网最新网红黑料与娱乐八卦资讯平台，7×24小时不间断更新。"
        keywords="黑料吃瓜网,关于我们,品牌介绍,联系方式,吃瓜平台"
        canonical="http://cgshe.cn/about"
        schemaJson={aboutSchema}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      <main>
        <section className="pt-20 md:pt-24 pb-14 md:pb-20">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-8">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700">关于我们</span>
              </nav>

              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                <div className="flex-1 lg:max-w-[40%]">
                  <span className="inline-block px-3 py-1 rounded-md border border-foreground-200 text-xs text-foreground-500 mb-4">
                    品牌介绍
                  </span>
                  <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight">
                    关于黑料吃瓜网
                    <span className="block text-foreground-400 text-2xl md:text-3xl mt-2">🔥 专业吃瓜平台</span>
                  </h1>
                  <p className="mt-6 text-sm md:text-base text-foreground-600 leading-relaxed">
                    黑料吃瓜网成立于2019年，是国内领先的娱乐八卦资讯聚合平台。我们致力于为吃瓜群众提供最新、最全、最可靠的网红爆料和明星八卦资讯。
                  </p>
                  <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    平台覆盖微博、抖音、快手、小红书、B站等50+主流社交平台，拥有专业编辑团队7×24小时不间断监控全网热点，确保您第一时间吃到最新鲜的瓜。
                  </p>
                  <a
                    href="#contact"
                    className="inline-block mt-6 text-sm text-primary-600 hover:text-primary-700 border-b border-primary-300 hover:border-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    联系我们 →
                  </a>
                </div>

                <div className="flex-1 lg:max-w-[55%]">
                  <div className="flex items-end justify-end gap-8 mb-6">
                    {aboutStats.slice(0, 2).map((stat) => (
                      <div key={stat.label} className="text-right">
                        <p className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">{stat.value}</p>
                        <p className="text-xs text-foreground-400 mt-1">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="relative flex gap-4">
                    <div className="w-40 h-48 md:w-48 md:h-56 rounded-2xl overflow-hidden mt-8">
                      <img
                        src="https://readdy.ai/api/search-image?query=Modern%20open%20office%20workspace%20with%20warm%20coral%20and%20cream%20tones%2C%20creative%20team%20collaboration%20atmosphere%2C%20soft%20natural%20lighting%2C%20editorial%20interior%20photography%2C%20minimalist%20design&width=400&height=500&seq=about-hero-01&orientation=portrait"
                        alt="黑料吃瓜网办公环境"
                        title="黑料吃瓜网团队"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="w-56 h-72 md:w-64 md:h-80 rounded-2xl overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Diverse%20creative%20team%20in%20modern%20office%20with%20warm%20lighting%2C%20collaborative%20atmosphere%2C%20coral%20and%20amber%20accent%20colors%2C%20editorial%20corporate%20photography%2C%20professional%20yet%20casual%20vibe&width=500&height=700&seq=about-hero-02&orientation=portrait"
                        alt="黑料吃瓜网团队协作"
                        title="黑料吃瓜网团队协作"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-10 md:mb-14">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-heading text-xs tracking-widest text-foreground-500 uppercase">
                    OUR ADVANTAGES
                  </span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950">
                  我们的优势
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {aboutAdvantages.map((adv) => (
                  <AdvantageCard key={adv.id} adv={adv} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-10 md:mb-14">
                <span className="inline-block px-4 py-1.5 rounded-lg bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  用户口碑
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight">
                  百万用户
                  <br />
                  真实评价
                </h2>
              </div>

              <div className="columns-1 md:columns-2 lg:columns-3 gap-5 md:gap-6">
                {reputationComments.map((comment) => (
                  <div
                    key={comment.id}
                    className="break-inside-avoid mb-5 md:mb-6 bg-background-50 border border-background-200/70 rounded-2xl p-5 md:p-6 hover:border-accent-200/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <span className="font-heading text-accent-600 text-sm">{comment.avatar}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{comment.name}</p>
                        <p className="text-xs text-foreground-400">{comment.handle}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      &ldquo;{comment.comment}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                <div>
                  <span className="inline-block px-4 py-1.5 rounded-lg bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                    联系我们
                  </span>
                  <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 leading-tight mb-6">
                    期待与您交流
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                    无论您是想爆料投稿、商务合作还是提供建议，
                    都欢迎通过以下方式联系我们。我们会在24小时内回复您的消息。
                  </p>

                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">电子邮箱</p>
                        <p className="text-sm text-foreground-950 font-medium">hello@cgshe.cn</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-wechat-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">微信公众号</p>
                        <p className="text-sm text-foreground-950 font-medium">黑料吃瓜网</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-weibo-line text-secondary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">官方微博</p>
                        <p className="text-sm text-foreground-950 font-medium">@黑料吃瓜网官微</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <h3 className="font-heading text-xl text-foreground-950 mb-6">发送消息</h3>
                  <form
                    className="flex flex-col gap-4"
                    onSubmit={handleSubmit}
                   
                    action={FORM_URL}
                    method="POST"
                  >
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-anti-spam-field"
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-xs text-foreground-500 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs text-foreground-500 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs text-foreground-500 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'loading' ? (
                        <>发送中...<i className="ri-loader-4-line ml-2 animate-spin"></i></>
                      ) : (
                        <>发送消息<i className="ri-send-plane-line ml-2"></i></>
                      )}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">消息发送成功！我们会尽快回复您。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="flex flex-col gap-4">
                {[
                  { q: '黑料吃瓜网的内容来源是什么？', a: '黑料吃瓜网的内容来源于全网社交平台公开信息、网友投稿以及专业娱乐记者的一手资讯。我们对每条爆料都进行多方核实，确保信息真实可靠。' },
                  { q: '如何向黑料吃瓜网投稿爆料？', a: '您可以通过我们的官方邮箱 hello@cgshe.cn 进行投稿，也可以在微博、微信公众号私信我们。我们承诺对爆料人信息严格保密。' },
                  { q: '黑料吃瓜网的更新频率是怎样的？', a: '黑料吃瓜网实行7×24小时不间断更新机制，编辑团队全天候监控全网热点，确保您在任何时间访问都能看到最新内容。每天平均更新300+条资讯。' },
                  { q: '黑料吃瓜网如何保证信息真实性？', a: '我们拥有一支专业的审核团队，每条爆料发布前都会经过至少两轮交叉验证。对于未经证实的信息，我们会明确标注"待核实"，绝不传播虚假内容。' },
                ].map((faq, idx) => (
                  <details
                    key={idx}
                    className="group border border-background-200/70 rounded-2xl overflow-hidden cursor-pointer"
                  >
                    <summary className="flex items-center justify-between px-5 md:px-6 py-4 text-sm md:text-base font-medium text-foreground-950 hover:text-primary-600 transition-colors">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 md:px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

function AdvantageCard({ adv }: { adv: typeof aboutAdvantages[0] }) {
  if (adv.type === 'dark') {
    return (
      <div className="group rounded-2xl bg-foreground-950 p-6 md:p-8 transition-transform duration-300 hover:-translate-y-1">
        <div className="w-12 h-12 rounded-xl bg-foreground-800/50 flex items-center justify-center mb-6">
          <span className="text-2xl">{adv.icon}</span>
        </div>
        <h3 className="font-heading text-xl md:text-2xl text-background-50 mb-3">
          {adv.title}
        </h3>
        <p className="text-sm text-foreground-300 leading-relaxed">
          {adv.description}
        </p>
      </div>
    );
  }

  if (adv.type === 'image') {
    return (
      <div className="group rounded-2xl overflow-hidden relative h-80 md:h-96 transition-transform duration-300 hover:-translate-y-1">
        <img
          src={adv.image}
          alt={adv.title}
          title={adv.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm text-xs text-background-50 font-medium">
            <i className="ri-star-line mr-1"></i>
            {adv.category}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="font-heading text-xl md:text-2xl text-background-50">
            {adv.title}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="group rounded-2xl bg-background-50 p-6 md:p-8 transition-transform duration-300 hover:-translate-y-1">
      <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
        {adv.title}
      </h3>
      <p className="text-sm text-foreground-600 leading-relaxed mb-6">
        {adv.description}
      </p>
      <div className="rounded-xl overflow-hidden h-40 md:h-48">
        <img
          src={adv.image}
          alt={adv.title}
          title={adv.title}
          className="w-full h-full object-cover object-top"
        />
      </div>
    </div>
  );
}